import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
  faSave,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { EmployeeAvailability } from "@/utils/types";
import { toast } from "sonner";

export interface EmployeeAvailabilityInputRef {
  saveAllAvailability: () => Promise<EmployeeAvailability[]>;
}

interface EmployeeAvailabilityInputProps {
  availabilityHours: EmployeeAvailability[];
  setAvailabilityHours: React.Dispatch<
    React.SetStateAction<EmployeeAvailability[]>
  >;
  employeeId: number;
  onSuccessfulSave?: (hours: EmployeeAvailability[]) => void;
}

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const getDayIndex = (day: string): number => {
  return daysOfWeek.indexOf(day);
};

const sortAvailabilityHours = (
  hours: EmployeeAvailability[],
): EmployeeAvailability[] => {
  return [...hours].sort((a, b) => {
    const dayDiff = getDayIndex(a.day) - getDayIndex(b.day);
    if (dayDiff !== 0) return dayDiff;
    return a.start_time.localeCompare(b.start_time);
  });
};

const formatTimeString = (timeString: string): string => {
  // If the time includes seconds (HH:MM:SS format), remove the seconds part
  if (timeString && timeString.length > 5 && timeString.includes(':')) {
    return timeString.substring(0, 5);
  }
  return timeString;
};

const EmployeeAvailabilityInput = forwardRef<
  EmployeeAvailabilityInputRef,
  EmployeeAvailabilityInputProps
>(({ availabilityHours, setAvailabilityHours, employeeId, onSuccessfulSave }, ref) => {
  const [axiosInstance] = useAxiosInstance();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [localHours, setLocalHours] =
    useState<EmployeeAvailability[]>(availabilityHours);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteHourId, setDeleteHourId] = useState<number | null>(null);

  const sortedLocalHours = useMemo(
    () => sortAvailabilityHours(localHours),
    [localHours],
  );

  useEffect(() => {
    setLocalHours(availabilityHours);
  }, [availabilityHours]);

  useEffect(() => {
    if (isDirty) {
      setAvailabilityHours(localHours);
    }
  }, [isDirty, localHours, setAvailabilityHours]);

  const handleAddAvailabilityHour = () => {
    const newHour: EmployeeAvailability = {
      id: -Date.now(),
      day: "Monday",
      start_time: "09:00",
      end_time: "17:00",
    };
    setLocalHours((prev) => [...prev, newHour]);
    setEditingId(newHour.id);
    setIsDirty(true);
  };

  const handleEditAvailabilityHour = (id: number) => {
    setEditingId(id);
  };

  const handleSaveAvailabilityHour = async (id: number) => {
    try {
      // If already saving, prevent duplicate save
      if (isSaving) {
        console.log("EmployeeAvailabilityInput: Already saving, skipping duplicate save request");
        return;
      }
      
      const hourToSave = localHours.find((hour) => hour.id === id);
      if (!hourToSave) return;

      // Check if axiosInstance is available
      if (!axiosInstance) {
        console.error("EmployeeAvailabilityInput: axiosInstance is not available");
        toast.error("Error: API client not available");
        return;
      }

      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/employee-availability/`;
      let savedHour: EmployeeAvailability;

      setIsSaving(true);

      if (id < 0) {
        // New hour
        const { id: _, ...hourData } = hourToSave;
        try {
          const response = await axiosInstance.post(apiUrl, {
            ...hourData,
            employee: employeeId,
          });
          savedHour = response.data;
          
          // Update hours with the saved hour (which has a proper ID now)
          const updatedHours = localHours.map(hour => 
            hour.id === id ? savedHour : hour
          );
          
          // Update both local state and parent component state
          setLocalHours(updatedHours);
          setAvailabilityHours(updatedHours);
          
          // Call the callback if provided
          if (onSuccessfulSave) {
            onSuccessfulSave(updatedHours);
          }
          
        } catch (error: any) {
          console.error("Error saving availability hour:", error);
          toast.error("Error saving availability hour:");
          setIsSaving(false);
          return;
        }
      } else {
        // Existing hour
        try {
          const response = await axiosInstance.patch(
            `${apiUrl}${id}/`,
            hourToSave,
          );
          savedHour = response.data;
          
          // Update hours with the updated hour
          const updatedHours = localHours.map(hour => 
            hour.id === id ? savedHour : hour
          );
          
          // Update both local state and parent component state
          setLocalHours(updatedHours);
          setAvailabilityHours(updatedHours);
          
          // Call the callback if provided
          if (onSuccessfulSave) {
            onSuccessfulSave(updatedHours);
          }
          
        } catch (error: any) {
          console.error("Error saving availability hour:", error);
          toast.error("Error saving availability hour:");
          setIsSaving(false);
          return;
        }
      }

      // Reset editing state
      setEditingId(null);
      
      // Clear dirty state
      setIsDirty(false);
      
      // Clear saving state
      setIsSaving(false);
      
    } catch (error) {
      console.error("Error saving availability hour:", error);
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setLocalHours(availabilityHours);
    setIsDirty(false);
  };

  const handleInputChange = (id: number, field: string, value: string) => {
    setLocalHours((prev) =>
      prev.map((hour) => (hour.id === id ? { ...hour, [field]: value } : hour)),
    );
    setIsDirty(true);
  };

  const handleDeleteAvailabilityHour = (id: number) => {
    setDeleteHourId(id);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteAvailabilityHour = async () => {
    if (deleteHourId === null) return;

    try {
      if (deleteHourId >= 0) {
        const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/employee-availability/${deleteHourId}/`;
        await axiosInstance.delete(apiUrl);
      }

      const updatedHours = localHours.filter(
        (hour) => hour.id !== deleteHourId,
      );
      setLocalHours(updatedHours);
      setAvailabilityHours(updatedHours);
      setIsDirty(true);

      // Call the callback if provided
      if (onSuccessfulSave) {
        onSuccessfulSave(updatedHours);
      }

      setShowDeleteConfirmation(false);
      setDeleteHourId(null);
    } catch (error) {
      console.error("Error deleting availability hour:", error);
      toast.error("Error deleting availability hour");
    }
  };

  const saveAllChanges = async (skipHourId: number | null = null) => {
    // If there are no hours to save or axiosInstance is not available, just return successfully
    if (localHours.length === 0 || !axiosInstance) {
      console.log("EmployeeAvailabilityInput: No hours to save or axiosInstance not available");
      return true;
    }
    
    setIsSaving(true);
    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/employee-availability/`;

      const updatedHours: EmployeeAvailability[] = [];

      for (const hour of localHours) {
        // Skip the hour that was just saved to avoid duplicate requests
        if (skipHourId !== null && hour.id === skipHourId) {
          updatedHours.push(hour);
          continue;
        }
        
        if (hour.id < 0) {
          // New hour, create with POST
          const { id, ...hourData } = hour;
          const response = await axiosInstance.post(apiUrl, {
            ...hourData,
            employee: employeeId,
          });
          updatedHours.push(response.data);
        } else {
          // Existing hour, update with PATCH
          const response = await axiosInstance.patch(
            `${apiUrl}${hour.id}/`,
            hour,
          );
          updatedHours.push(response.data);
        }
      }

      // Update both local state and parent component state with saved data
      setLocalHours(updatedHours);
      setAvailabilityHours(updatedHours);
      setIsDirty(false);
      setEditingId(null);
      
      // Call the callback if provided
      if (onSuccessfulSave) {
        onSuccessfulSave(updatedHours);
      }
      
      // Return true to indicate success
      return true;
    } catch (error) {
      console.error("Error saving availability hours:", error);
      toast.error("Error saving availability hours");
      // Return false to indicate failure
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const discardChanges = () => {
    setLocalHours(availabilityHours);
    setIsDirty(false);
    setEditingId(null);
  };

  const renderAvailabilityHour = (hour: EmployeeAvailability) => {
    const isEditing = editingId === hour.id;

    return (
      <div key={hour.id} className="mb-4 overflow-hidden rounded-lg bg-gray-50">
        <div className="flex flex-col p-4 md:flex-row md:items-center md:space-x-4">
          <div className="mb-4 flex-grow text-2xl md:mb-0 md:grid md:grid-cols-3 md:gap-4 md:text-2xl">
            <div className="mb-2 font-medium md:mb-0">{hour.day}</div>
            <div className="flex justify-between md:col-span-2">
              <span>{formatTimeString(hour.start_time)}</span>
              <span className="mx-2"> - </span>
              <span>{formatTimeString(hour.end_time)}</span>
            </div>
          </div>
          <div className="flex items-center justify-center space-x-4 md:justify-end md:space-x-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => handleSaveAvailabilityHour(hour.id)}
                  className="flex-1 rounded bg-red-500 p-2 text-white transition-colors hover:bg-red-600 md:flex-none"
                >
                  <FontAwesomeIcon icon={faSave} />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 rounded bg-gray-500 p-2 text-white transition-colors hover:bg-gray-600 md:flex-none"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleEditAvailabilityHour(hour.id)}
                  className="flex-1 rounded bg-gray-500 p-2 text-white transition-colors hover:bg-gray-600 md:flex-none"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={() => handleDeleteAvailabilityHour(hour.id)}
                  className="flex-1 rounded bg-red-500 p-2 text-white transition-colors hover:bg-red-600 md:flex-none"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEditingHour = (hour: EmployeeAvailability) => {
    return (
      <div key={hour.id} className="mb-4 overflow-hidden rounded-lg bg-gray-50">
        <div className="flex flex-col p-4 md:flex-row md:items-center md:space-x-4">
          <div className="mb-4 grid flex-grow grid-cols-1 gap-4 md:mb-0 md:grid-cols-3">
            <div>
              <select
                value={hour.day}
                onChange={(e) =>
                  handleInputChange(hour.id, "day", e.target.value)
                }
                className="text- w-full rounded border p-2"
              >
                {daysOfWeek.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-between space-x-2 md:col-span-2">
              <input
                type="time"
                value={hour.start_time}
                onChange={(e) =>
                  handleInputChange(hour.id, "start_time", e.target.value)
                }
                className="text-md flex-1 rounded border p-2"
              />
              <span className="mx-2 flex items-center"> - </span>
              <input
                type="time"
                value={hour.end_time}
                onChange={(e) =>
                  handleInputChange(hour.id, "end_time", e.target.value)
                }
                className="text-md flex-1 rounded border p-2"
              />
            </div>
          </div>
          <div className="flex items-center justify-center space-x-4 md:justify-end md:space-x-2">
            <button
              type="button"
              onClick={() => handleSaveAvailabilityHour(hour.id)}
              className="flex-1 rounded bg-red-500 p-2 text-white transition-colors hover:bg-red-600 md:flex-none"
            >
              <FontAwesomeIcon icon={faSave} />
            </button>
            <button
              type="button"
              onClick={handleCancelEdit}
              className="flex-1 rounded bg-gray-500 p-2 text-white transition-colors hover:bg-gray-600 md:flex-none"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // This is the function that will be exposed to the parent component
  const saveAllAvailability = async (): Promise<EmployeeAvailability[]> => {
    console.log("EmployeeAvailabilityInput: saveAllAvailability called");
    
    // If there's no editing in progress and no changes to availability hours, just return current hours
    if (!editingId && !isDirty) {
      console.log("EmployeeAvailabilityInput: No pending changes, returning current hours");
      return localHours;
    }
    
    let justSavedHourId = null;
    
    if (editingId) {
      // First save the currently editing hour if any
      console.log("EmployeeAvailabilityInput: Saving currently editing hour first");
      await handleSaveAvailabilityHour(editingId);
      justSavedHourId = editingId;
      
      // Reset editing state after saving the current hour
      setEditingId(null);
    }
    
    // Then save all hours except the one we just saved
    console.log("EmployeeAvailabilityInput: Saving all availability hours");
    const result = await saveAllChanges(justSavedHourId);
    
    if (result) {
      // Clear the dirty state and return the saved hours
      console.log("EmployeeAvailabilityInput: All hours saved successfully");
      setIsDirty(false);
      return localHours;
    } else {
      console.error("EmployeeAvailabilityInput: Failed to save all hours");
      throw new Error("Failed to save availability hours");
    }
  };
  
  // Expose the function through the ref
  useImperativeHandle(ref, () => ({
    saveAllAvailability,
  }), [localHours, editingId]);

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow">
      <div className="p-4 md:mx-auto md:max-w-3xl md:p-6">
        <h3 className="mb-4 text-xl font-bold md:mb-6">Availability Hours</h3>
        <div className="space-y-2 md:space-y-4">
          {sortedLocalHours.map((hour) =>
            editingId === hour.id
              ? renderEditingHour(hour)
              : renderAvailabilityHour(hour),
          )}
        </div>
        <button
          type="button"
          onClick={handleAddAvailabilityHour}
          className="mt-4 w-full rounded bg-red-500 p-2 text-white transition-colors hover:bg-red-600 md:mt-6 md:w-auto"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Availability Hour
        </button>
      </div>

      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-xl font-bold">Confirm Deletion</h2>
            <p className="mb-6">
              Are you sure you want to delete this availability hour?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmation(false)}
                className="rounded-md bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteAvailabilityHour}
                className="rounded-md bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default EmployeeAvailabilityInput;
